import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { BenefitIcon } from "@/components/BenefitIcon";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldCheck, ArrowRight, Inbox } from "lucide-react";

const STATUS_STYLES = {
  active: "bg-green-100 text-green-700 border-green-200",
  waived: "bg-mist-200 text-navy-700 border-mist-300",
  draft: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function Enrollments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/enrollments").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const active = items.filter((i) => i.status === "active");
  const totalMonthly = active.reduce((sum, i) => {
    const opt = (i.product?.coverage_options || []).find((o) => o.tier === i.coverage_tier);
    return sum + (opt?.monthly_premium || 0);
  }, 0);

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-5xl mx-auto animate-rise">
      <PageHeader
        eyebrow="Enrollment status"
        title="My Coverage"
        subtitle="Everything you've elected, in one place. Review your active benefits and confirmation details."
      />

      {!loading && active.length > 0 && (
        <div className="bg-navy-800 rounded-2xl p-6 mb-6 flex items-center justify-between" data-testid="coverage-summary">
          <div>
            <p className="text-mist-300 text-sm">Active benefits</p>
            <p className="font-display text-3xl font-bold text-white">{active.length}</p>
          </div>
          <div className="text-right">
            <p className="text-mist-300 text-sm">Estimated monthly premium</p>
            <p className="font-display text-3xl font-bold text-gold">${totalMonthly.toFixed(2)}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-24 w-full" /></div>
      ) : items.length ? (
        <div className="space-y-3">
          {items.map((it) => {
            const opt = (it.product?.coverage_options || []).find((o) => o.tier === it.coverage_tier);
            return (
              <div key={it.id} className="bg-white rounded-xl border border-mist-200 p-5 flex items-center gap-4" data-testid={`enrollment-${it.id}`}>
                <div className="w-12 h-12 rounded-xl bg-mist-100 flex items-center justify-center shrink-0">
                  <BenefitIcon name={undefined} className="w-6 h-6 text-navy-800" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-navy-900 truncate">{it.product?.name}</p>
                    <DemoBadge />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {it.carrier?.name} {opt ? `· ${opt.label} · $${(opt.monthly_premium || 0).toFixed(2)}/mo` : ""}
                  </p>
                  {it.confirmation_number && (
                    <p className="text-xs text-muted-foreground mt-0.5">Confirmation {it.confirmation_number} · Effective {it.effective_date}</p>
                  )}
                </div>
                <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border capitalize ${STATUS_STYLES[it.status] || STATUS_STYLES.draft}`}>
                  {it.status}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-dashed border-mist-300 p-12 text-center" data-testid="empty-coverage">
          <Inbox className="w-10 h-10 text-mist-300 mx-auto mb-3" />
          <p className="font-semibold text-navy-900">No coverage yet</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Browse your available benefits and make your first election.</p>
          <Link to="/app/benefits">
            <Button className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="browse-benefits">
              Explore benefits <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
